import * as React from "react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { UrbanFeaturePolygonFormValues, urbanFeaturePolygonSchema } from "./urban-feature-form.schema";

import { AddressComponent } from "@/components/AddressComponent";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { UrbanFeature } from "@/modules/urban-feature/urban-feature.types";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

interface UrbanFeaturePromptProps {
  urbanFeature: UrbanFeature;
  onSave: (input: UrbanFeature) => Promise<void>;
  onClose: () => void;
  title: string;
}

export const UrbanFeaturePolygonModal = ({ urbanFeature, onClose, onSave, title }: UrbanFeaturePromptProps) => {
  const form = useForm<UrbanFeaturePolygonFormValues>({
    resolver: zodResolver(urbanFeaturePolygonSchema),
    defaultValues: {
      ...urbanFeature,
      name: urbanFeature.name ?? undefined,
      width: urbanFeature.width ?? 2,
    },
    mode: "all",
  });

  const {
    formState: { isSubmitting },
    control,
  } = form;

  const handleClose = () => {
    onClose();
  };

  const onSubmit = async (input: UrbanFeaturePolygonFormValues) => {
    const formattedInput = {
      ...urbanFeature,
      ...input,
    };
    await onSave({ ...formattedInput, cityId: urbanFeature.cityId });
  };

  return (
    <Dialog open={true}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{`${urbanFeature?.id ? "Edit " : "Add "}a ${title}`}</DialogTitle>
          <DialogDescription>Crosswalk information</DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <FormField
              control={control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <AddressComponent />
            <FormField
              control={form.control}
              name="width"
              render={({ field: { onChange, ...agr } }) => (
                <FormItem>
                  <FormLabel>Largeur</FormLabel>
                  <FormControl>
                    <Input
                      {...agr}
                      type="number"
                      onChange={(e) => {
                        onChange(Number(e.target.value));
                      }}
                      min={0}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <DialogFooter className="sm:justify-between">
              <Button onClick={handleClose} variant="secondary" type="button">
                Cancel
              </Button>
              <Button disabled={isSubmitting} type="submit">
                Save
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
